import { type TwilioCredentials } from 'app/hooks/fetch/useFetchLiveChat';
import isTestParam from 'app/copied/isTestParam';

import { getBrowserFingerprint } from '../copied/fingerprint';
import { waitForIDs } from '../copied/getIds';
import { ChatModuleType, SuggestedEntry } from '../copied/models/ChatModules';
import ISIConfig from '../copied/models/ISIConfig';
import { ChatCustomization } from '../copied/types/ChatItems';
import makeApi from './api';

export enum FooterLinkType {
  REP_CHAT_PRIMARY = 'rep_chat_primary',
  REP_CHAT_SECONDARY = 'rep_chat_secondary',
}

export interface FooterLink {
  link: string;
  logo: string;
  show: boolean;
  text: string;
  type: FooterLinkType;
  action_type?: ModuleItemActionType;
  description?: string;
}

export interface Category {
  id: string;
  name: string;
  article_count: number;
  icon: string;
}

export enum ModuleItemActionType {
  OPEN_VIRTUAL_ASSISTANT = 'open_virtual_assistant',
  OPEN_EXTERNAL_LINK = 'open_external_link',
  OPEN_CONTACT_FORM = 'rep_chat_contact_forms',
}

export enum PillType {
  SEARCH_TEXT = 'search_text',
  VA_NODE = 'va_node',
}

export interface ModuleItem {
  action?: string;
  action_obj:
    | {
        action_type: ModuleItemActionType.OPEN_EXTERNAL_LINK;
        url: string;
      }
    | {
        action_type: ModuleItemActionType.OPEN_VIRTUAL_ASSISTANT;
        url?: string;
      };
  icon?: string;
  title: string;
  description?: string;
  module_type: string;
  primary_hub?: boolean;
  hub_condition_uuid?: string;
  indication_name?: string;
  display_name?: string;
  background_color?: string;
  border_color?: string;
  border_size?: number;
  border_radius?: number;
  pill_type?: PillType;
  pill_data?: string;
  pill_text?: string;
}

export type ChatModuleActiveRule = 'always' | 'nn_offline' | '';

export type NudgeAnimationOptions = {
  enable_animation: boolean;
  animation_style: string; // 'bouncy';
  animation_duration: number; // number in milliseconds
  animation_repeats: number; // number of times the animation repeats
  repeat_delay: number; // number in milliseconds
  reset_interval: number; // number in minutes
  enable_desktop_animation: boolean;
};

export interface ChatModule {
  module_type: ChatModuleType;
  document_categories?: Category[];
  items?: ModuleItem[];
  header_title?: string;
  header_icon?: string;
  is_active_rule?: ChatModuleActiveRule;
  config?: {
    suggested_entries: SuggestedEntry[];
    enable_hub_pdf_viewer?: boolean;
    enable_hub_pdf_tags?: boolean;
  };
  va_as_landing_page_enabled?: boolean;
  va_allow_use_crafted_responses?: boolean;
  va_convo_ai_enabled?: boolean;
  disclaimer_enabled?: boolean;
  disclaimer_text?: string;
  primary_selector_enabled?: boolean;
  secondary_selector_enabled?: boolean;
  search_suggestions?: { text: string }[];
}

export interface InterstitialPage {
  button_text: string;
  content_text: string;
  display_interstitial: boolean;
  header_text: string;
  domains?: string[];
}

export interface HelpCenterResponse {
  uuid: string;
  title: string;
  valid_until_date_str: string;
  contact_forms_include_isi: boolean;
  customization: ChatCustomization;
  header: {
    lines: string[];
    header_icon?: string;
    header_text_enabled?: boolean;
  };
  interstitial_page?: InterstitialPage;
  domain_interstitial_pages?: InterstitialPage[];
  external_url_param_config: {
    enabled: boolean;
    name: string;
    value: string;
  };
  options: {
    autoopen_chat: boolean;
    landing_button_title: string;
    main_screen_enabled: boolean;
    autoopen_desktop_delay_ms: number;
    autoopen_mobile_delay_ms: number;
    autoopen_mobile_disabled: boolean;
    autoopen_chat_rule: 'half_open' | 'full_open';
    nudge_animation: NudgeAnimationOptions;
  };
  isi_block: ISIConfig;
  footer: {
    lines: string[];
    footer_links: FooterLink[];
  };
  logo: {
    url: string;
    link: string;
  };
  modules: ChatModule[];
  next_best_action: {
    enable_next_best_resource: boolean;
    enable_outside_next_best_resource: boolean;
    highlight_color: string;
    hardcoded_outside_suggestions: Record<string, SuggestedEntry[]>;
    auto_collapse_nba: boolean;
    delay_before_show: number;
    delay_before_collapse: number;
    nba_font_color: string;
    open_nba_button_background_color: string;
    open_nba_button_icon: string;
  };
  nurses_are_online: boolean;
  twilio_credentials: TwilioCredentials;
}

export default async function fetchHelpCenter(): Promise<HelpCenterResponse> {
  const [{ conditionId, rxId }, fingerprint] = await Promise.all([waitForIDs(), getBrowserFingerprint()]);

  window.ostro?.analytics?.addPersistentParams?.({ conditionUUID: conditionId, RxID: rxId });

  const user_timezone: string | null = Intl.DateTimeFormat()?.resolvedOptions()?.timeZone ?? null;
  const test_only = isTestParam();

  const urlParams = new URLSearchParams(window.location.search);
  const recoveryToken = urlParams.get('link_token') || '';

  const payload: Record<string, unknown> = {
    condition: conditionId,
    incoming_url: window.location.href,
    rx_user_uuid: rxId ? rxId : undefined,
    fingerprint,
    user_timezone,
    test_only,
  };

  if (recoveryToken) {
    payload.link_token = recoveryToken;
  }

  const api = makeApi();
  const response = await api.post(`/api/v1/web_chat/v3.0/help-center/`, payload);

  // expose TAIlor configuration
  window.TAIlor?.exposeTAIlorConfiguration(response.data);

  return response.data;
}
